import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PostService } from '../../../services/post-service';
import { map, Subscription } from 'rxjs';
import { Post, PostPage } from '../../../models/post.model';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocalDatePipe } from '../../../shared/pipes/local-date-pipe';
import { Translatable } from '../../../models/translatable.model';
import { Media } from '../../../models/media.model';
import { TruncatePipe } from '../../../shared/pipes/truncate-pipe';
import { SidebarBlog } from '../sidebar-blog/sidebar-blog';
import { Loading } from '../../layouts/loading/loading';

/** Mapping slug → clé i18n pour le titre de page */
const SLUG_TITLE_KEY: Record<string, string> = {
  actualites:  'pub_actualites',
  evenements:  'pub_evenements',
  communiques: 'pub_communiques',
};

@Component({
  selector: 'app-articles-component',
  standalone: true,
  imports: [CommonModule, TranslateModule, LocalDatePipe,  RouterModule, SidebarBlog, Loading],//TruncatePipe,
  providers: [DatePipe],
  templateUrl: './articles-component.html',
  styleUrl: './articles-component.css',
})
export class ArticlesComponent implements OnInit, OnDestroy {
  page      = 1;
  pageSize  = 5;
  total     = 0;
  articles: Post[] = [];
  loading   = true;

  searchQuery   = '';
  categoryId?: number;
  categorySlug  = '';

  private paramsSub?: Subscription;

  constructor(
    private postsService: PostService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.paramsSub = this.route.queryParams.subscribe(params => {
      this.searchQuery  = params['q']             ?? '';
      this.categoryId   = params['category_id']   ? +params['category_id'] : undefined;
      this.categorySlug = params['category_slug'] ?? '';
      this.page = 1;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.paramsSub?.unsubscribe();
  }

  load(): void {
    this.loading = true;
    this.postsService
      .getArticles(this.page, this.pageSize, this.searchQuery, this.categoryId, this.categorySlug)
      .subscribe({
        next: (res: PostPage) => {
          this.articles = res.data;
          this.total    = res.total;
          this.loading  = false;
        },
        error: () => { this.loading = false; },
      });
  }

  loadPage(page: number): void {
    if (page < 1) return;
    this.page = page;
    this.load();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  /** Pages à afficher : ±2 autour de la page courante (comme /loi) */
  get pages(): number[] {
    const range: number[] = [];
    const delta = 2;
    for (
      let i = Math.max(1, this.page - delta);
      i <= Math.min(this.totalPages, this.page + delta);
      i++
    ) {
      range.push(i);
    }
    return range;
  }

  /** Clé i18n du titre de la page selon le slug de catégorie actif */
  get pageTitleKey(): string {
    return SLUG_TITLE_KEY[this.categorySlug] ?? 'sidebar_blog_titre';
  }

  /** True si un filtre quelconque est actif */
  get hasFilter(): boolean {
    return !!(this.searchQuery || this.categoryId || this.categorySlug);
  }

  formatDate(value: string): string {
    return this.datePipe.transform(value, 'dd MMM, yy', undefined, 'fr-FR') ?? '';
  }

  translateField(field: Translatable | null | undefined): string {
    if (!field) return '';
    const lang = this.translate.getCurrentLang();
    return (field as any)[lang] || field.fr || field.en || '';
  }

  loadImageFeatured(imageUrl: Media | null | undefined): string {
    return imageUrl ? imageUrl.url : '/assets/images/default_image.png';
  }
}
